import axios, { AxiosResponse, AxiosError } from 'axios';
import type { InternalAxiosRequestConfig } from 'axios';
import {
    ShoppingList,
    ShoppingListWithItems,
    ShoppingListItem,
    User,
    ReferralCodeInfo,
    ReferralStats,
    ReferralClickResult,
    ReferralSignupResult,
    ImageModelConfig,
    ImageModelRegistrySummary,
    ImageModelUpdatePayload,
    TierModelChainConfig,
    TierModelChainRecord,
    ImageModelEvaluationDetail,
    ImageModelEvaluationListResult,
    ImageModelEvaluationRatingPayload,
    ImageModelEvaluationResolvePayload,
    ImageModelEvaluationResult,
    ImageModelEvaluationRun,
} from '../types';
import type { UserImageLimits } from '@UserImageLimits';

// In development, use relative URLs to go through the Vite proxy
// In production, use the full API URL
const API_URL = import.meta.env.DEV ? '' : (import.meta.env.VITE_API_URL || 'http://localhost:3001');
const API_KEY = import.meta.env.VITE_API_KEY;

// Helper to convert Axios headers to plain object for fetch
function headersToPlainObject(headers: unknown): Record<string, string> {
    if (!headers) return {};
    if (typeof headers === 'object' && !(headers instanceof Headers)) {
        return Object.fromEntries(Object.entries(headers).map(([k, v]) => [k, String(v)]));
    }
    return {};
}

// Custom fetch adapter for Axios to ensure service worker interception
const fetchAdapter = async (config: InternalAxiosRequestConfig): Promise<AxiosResponse> => {
    const { url, method, data, headers } = config;

    if (!url) throw new Error('Request URL is required');

    // Create fetch options
    const fetchOptions: RequestInit = {
        method: method?.toUpperCase(),
        headers: headersToPlainObject(headers),
    };

    // Add body for non-GET requests
    if (data && method?.toUpperCase() !== 'GET') {
        fetchOptions.body = typeof data === 'string' ? data : JSON.stringify(data);
    }

    try {
        const response = await fetch(url, fetchOptions);

        // Convert Headers to plain object for axios compatibility (needed whether we throw or return)
        const responseHeaders: Record<string, string> = {};
        response.headers.forEach((value, key) => {
            responseHeaders[key] = value;
        });

        // Parse body helper (json → object | text)
        const parseBody = async () => {
            if (response.status === 204 || response.headers.get('content-length') === '0') {
                return null;
            }
            const contentType = response.headers.get('content-type') ?? '';
            if (contentType.includes('application/json')) {
                return await response.json();
            }
            return await response.text();
        };

        // For non-2xx statuses, mimic Axios behaviour by rejecting
        if (!response.ok) {
            const errorData = await parseBody();

            const axiosError = new Error(errorData?.message || response.statusText) as AxiosError;
            axiosError.response = {
                data: errorData,
                status: response.status,
                statusText: response.statusText,
                headers: responseHeaders,
                config,
                request: response
            };
            axiosError.config = config;
            axiosError.isAxiosError = true;
            throw axiosError;
        }

        // For successful responses, return like Axios would
        const responseData = await parseBody();

        return {
            data: responseData,
            status: response.status,
            statusText: response.statusText,
            headers: responseHeaders,
            config,
            request: response
        };
    } catch (error: unknown) {
        // Handle fetch errors
        if (typeof error === 'object' && error !== null) {
            const err = error as { message?: string; code?: string; name?: string };
            if (err.name === 'AbortError') {
                throw new axios.Cancel('Request timeout');
            }
            if ((err.message === 'Failed to fetch') || (err.code === 'ERR_NETWORK')) {
                // In offline mode, Workbox background sync will handle this request
                // We don't want to log confusing errors since the request will be queued

                // Create a special error that indicates this is a queued request
                const queuedError = new Error('Request queued for background sync') as Error & { isQueued: boolean };
                queuedError.name = 'QueuedRequest';
                queuedError.isQueued = true;
                throw queuedError;
            }
        }
        // For other errors, log them normally
        if (process.env.NODE_ENV === 'development') {
            console.log('[API] ❌ Fetch error:', error);
        }
        throw error;
    }
};

// Create axios instance with custom adapter
const api = axios.create({
    baseURL: API_URL,
    headers: {
        'Content-Type': 'application/json',
        'x-api-key': API_KEY,
    },
    adapter: fetchAdapter, // Use custom fetch adapter
    timeout: 10000, // 10 second timeout to prevent hanging requests
});

// Auto-refresh utility
const refreshTokens = async (): Promise<{ token: string; refreshToken: string } | null> => {
    const refreshToken = localStorage.getItem('refreshToken');
    if (!refreshToken) return null;

    try {
        const response = await fetch(`${API_URL}/api/auth/refresh`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'x-api-key': API_KEY || '',
            },
            body: JSON.stringify({ refreshToken }),
        });

        if (!response.ok) {
            throw new Error('Token refresh failed');
        }

        const data = await response.json();
        return data;
    } catch (error) {
        if (process.env.NODE_ENV === 'development') {
            console.error('Token refresh failed:', error);
        }
        return null;
    }
};

// Request interceptor to add auth token
api.interceptors.request.use(
    (config) => {
        config.headers = config.headers || {};
        // Add a unique request ID for tracing
        config.headers['X-Request-ID'] = crypto.randomUUID();

        const token = localStorage.getItem('token');
        if (token) {
            config.headers['x-auth-token'] = token;

            // Add dev-user-id header for development mode
            if (process.env.NODE_ENV === 'development' && token === 'dev-test-token') {
                const devUserId = localStorage.getItem('dev-user-id');
                if (devUserId) {
                    config.headers['x-dev-user-id'] = devUserId;
                }
            }
        }
        // Ensure API key is always present
        config.headers['x-api-key'] = API_KEY;

        // Add user language to headers
        const userLanguage = localStorage.getItem('i18nextLng') || 'en';
        config.headers['x-user-language'] = userLanguage;

        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

// Response interceptor to handle token refresh
api.interceptors.response.use(
    (response) => response,
    async (error) => {
        const originalRequest = error.config;

        // Check if error is due to expired token and we haven't already tried to refresh
        if (error.response?.status === 401 &&
            (error.response?.data?.code === 'TOKEN_EXPIRED' || error.response?.data?.code === 'INVALID_TOKEN') &&
            !originalRequest._retry) {

            originalRequest._retry = true;

            if (process.env.NODE_ENV === 'development') {
                console.log('Token expired, attempting to refresh...');
            }
            const newTokens = await refreshTokens();

            if (newTokens) {
                localStorage.setItem('token', newTokens.token);
                localStorage.setItem('refreshToken', newTokens.refreshToken);

                // Update the original request with new token
                originalRequest.headers['x-auth-token'] = newTokens.token;

                if (process.env.NODE_ENV === 'development') {
                    console.log('Token refreshed, retrying original request...');
                }
                return api(originalRequest);
            } else {
                // Refresh failed, clear tokens and redirect to login
                localStorage.removeItem('token');
                localStorage.removeItem('refreshToken');

                // Trigger a page reload to let auth context handle the logout
                window.location.href = '/login?reason=session_expired';
                return Promise.reject(error);
            }
        }

        return Promise.reject(error);
    }
);

// Auth API
export const getCurrentUser = async (): Promise<User> => {
    const response = await api.get('/api/auth/me');
    return response.data;
};

// Shopping Lists API
export const getLists = async (): Promise<ShoppingList[]> => {
    const response = await api.get('/api/shopping-lists');
    return response.data;
};

export const getList = async (id: string): Promise<ShoppingListWithItems> => {
    const response = await api.get(`/api/shopping-lists/${id}`);
    return response.data;
};

// Shared Lists API
export const shareList = async (id: string, email: string): Promise<{ success: boolean }> => {
    const response = await api.post(`/api/shopping-lists/${id}/share`, { email });
    return response.data;
};

export const unshareList = async (id: string, userId: string): Promise<void> => {
    await api.delete(`/api/shopping-lists/${id}/share/${userId}`);
};

export const getCollaborators = async (id: string): Promise<{ collaborators: { userId: string; email?: string | null; name?: string | null; avatarUrl?: string | null }[]; recent?: { userId: string; email?: string | null; name?: string | null; avatarUrl?: string | null }[] }> => {
    const response = await api.get(`/api/shopping-lists/${id}/collaborators`);
    return response.data;
};

export const createList = async (
    name: string,
    options?: { createdAt?: number; listType?: string; selectedCharacterId?: string | null } | number
): Promise<ShoppingList> => {
    const payload: { name: string; createdAt?: number; listType?: string; selectedCharacterId?: string | null } = { name };
    if (typeof options === 'number') {
        payload.createdAt = options;
    } else if (options && typeof options === 'object') {
        if (options.createdAt) payload.createdAt = options.createdAt;
        if (options.listType) payload.listType = options.listType;
        if (options.selectedCharacterId !== undefined) payload.selectedCharacterId = options.selectedCharacterId;
    }
    const response = await api.post('/api/shopping-lists', payload);
    return response.data;
};

export const deleteList = async (id: string): Promise<void> => {
    await api.delete(`/api/shopping-lists/${id}`);
};

export const archiveList = async (id: string): Promise<{ message: string; archived: boolean }> => {
    const response = await api.patch(`/api/shopping-lists/${id}/archive`);
    return response.data;
};

export const unarchiveList = async (id: string): Promise<{ message: string; archived: boolean }> => {
    const response = await api.patch(`/api/shopping-lists/${id}/unarchive`);
    return response.data;
};

// Shopping List Items API
export const addItem = async (listId: string, name: string, createdAt?: number, promptId?: string, selectedCharacterId?: string): Promise<ShoppingListItem & { limits?: UserImageLimits }> => {
    const payload: { name: string; createdAt?: number; promptId?: string; selectedCharacterId?: string } = { name };
    if (createdAt) {
        payload.createdAt = createdAt;
    }
    if (promptId) {
        payload.promptId = promptId;
    }
    if (selectedCharacterId) {
        payload.selectedCharacterId = selectedCharacterId;
    }
    const response = await api.post(`/api/shopping-lists/${listId}/items`, payload);
    return response.data;
};

// User limits API
export const getUserImageLimits = async (): Promise<UserImageLimits> => {
    const response = await api.get('/api/shopping-lists/user/limits');
    return response.data;
};

// Default image API
export const getDefaultImage = async (itemName: string): Promise<{
    imageUrl: string;
    contentType: string;
    isDefault: boolean;
    remainingImages: number;
    limitReached: boolean;
}> => {
    const response = await api.get(`/api/shopping-lists/images/default/${encodeURIComponent(itemName)}`);
    return response.data;
};

// Image cache for better performance
class ImageCache {
    private cache = new Map<string, { imageUrl: string; contentType: string; timestamp: number }>();
    private readonly CACHE_DURATION = 60 * 60 * 1000; // 1 hour in milliseconds
    private stats = { hits: 0, misses: 0, sets: 0 };

    set(imageId: string, data: { imageUrl: string; contentType: string }) {
        this.cache.set(imageId, {
            ...data,
            timestamp: Date.now()
        });
        this.stats.sets++;
    }

    get(imageId: string): { imageUrl: string; contentType: string } | null {
        const cached = this.cache.get(imageId);
        if (!cached) {
            this.stats.misses++;
            return null;
        }

        // Check if cache is still valid
        if (Date.now() - cached.timestamp > this.CACHE_DURATION) {
            this.cache.delete(imageId);
            this.stats.misses++;
            return null;
        }

        this.stats.hits++;
        return {
            imageUrl: cached.imageUrl,
            contentType: cached.contentType
        };
    }

    clear() {
        this.cache.clear();
    }

    // Delete a specific image from cache
    delete(imageId: string) {
        this.cache.delete(imageId);
    }

    // Remove expired entries
    cleanup() {
        const now = Date.now();
        for (const [key, value] of this.cache.entries()) {
            if (now - value.timestamp > this.CACHE_DURATION) {
                this.cache.delete(key);
            }
        }
    }

    // Get cache statistics
    getStats() {
        const total = this.stats.hits + this.stats.misses;
        const hitRate = total > 0 ? (this.stats.hits / total * 100).toFixed(2) : '0.00';
        return {
            ...this.stats,
            hitRate: `${hitRate}%`,
            size: this.cache.size
        };
    }
}

// Global image cache instance
const imageCache = new ImageCache();

// Clean up expired cache entries periodically
// Avoid starting background timers during tests to prevent open-handle leaks
const isVitest = typeof import.meta !== 'undefined' && !!(import.meta as any).vitest;
let cleanupInterval: any | undefined;
if (!isVitest) {
    cleanupInterval = setInterval(() => imageCache.cleanup(), 5 * 60 * 1000); // Every 5 minutes
    // In Node contexts, unref to avoid keeping the event loop alive
    if (cleanupInterval && typeof cleanupInterval.unref === 'function') {
        cleanupInterval.unref();
    }
}

// Cache invalidation function
export const invalidateImageCache = (imageId?: string) => {
    if (imageId) {
        imageCache.delete(imageId);
    } else {
        imageCache.clear();
    }
};

// Get cache statistics for debugging
export const getImageCacheStats = () => {
    return imageCache.getStats();
};

// Image API
export const getImage = async (imageId: string, useThumbnail: boolean = true): Promise<{
    imageUrl: string;
    contentType: string;
    thumbnailUrl?: string;
    thumbnailProcessed?: boolean;
}> => {
    // Check cache first
    const cacheKey = `${imageId}-${useThumbnail}`;
    const cached = imageCache.get(cacheKey);
    if (cached) {
        return cached;
    }

    // Fetch from API if not in cache
    const params = useThumbnail ? { thumbnail: 'true' } : {};
    const response = await api.get(`/api/shopping-lists/images/${imageId}`, { params });
    const data = response.data;

    // Store in cache
    imageCache.set(cacheKey, data);

    return data;
};

// Get full-size image (for modal)
export const getFullImage = async (imageId: string): Promise<{
    imageUrl: string;
    contentType: string;
    thumbnailUrl?: string;
    thumbnailProcessed?: boolean;
}> => {
    return getImage(imageId, false);
};

export const regenerateImage = async (listId: string, itemId: string): Promise<ShoppingListItem & { limits?: UserImageLimits }> => {
    const response = await api.post(`/api/shopping-lists/${listId}/items/${itemId}/regenerate`);

    // Invalidate cache for the old image if it exists
    const oldItem = response.data as ShoppingListItem;
    if (oldItem.imageId) {
        invalidateImageCache(oldItem.imageId);
    }

    return response.data;
};

export const deleteItem = async (listId: string, itemId: string): Promise<void> => {
    await api.delete(`/api/shopping-lists/${listId}/items/${itemId}`);
};

export const toggleItemChecked = async (listId: string, itemId: string): Promise<ShoppingListItem> => {
    const response = await api.patch(`/api/shopping-lists/${listId}/items/${itemId}/toggle`);
    return response.data;
};

// Image styles & prompts API
export type PredefinedStyle = {
    id: string;
    name: string;
    template: string;
    imageUrl?: string;
    isSystem?: boolean;
    promptKind?: 'style' | 'character';
};
export type UserPrompt = {
    userId: string;
    promptId: string;
    promptName: string;
    promptTemplate: string;
    createdAt: string;
    updatedAt: string;
    promptKind?: 'style' | 'character';
    refImageUrl?: string;
    thumbnailUrl?: string;
};

export const getUserPrompts = async (promptKind?: 'style' | 'character'): Promise<{ predefined: PredefinedStyle[]; custom: UserPrompt[] }> => {
    const url = promptKind
        ? `/api/shopping-lists/user-prompts?promptKind=${promptKind}`
        : '/api/shopping-lists/user-prompts';
    const response = await api.get(url);
    return response.data;
};

export const createUserPrompt = async (data: { promptName: string; promptTemplate: string; promptKind?: 'style' | 'character' }): Promise<{ success: boolean; prompt: UserPrompt }> => {
    const response = await api.post('/api/shopping-lists/user-prompts', data);
    return response.data;
};

export const updateUserPrompt = async (promptId: string, data: { promptName?: string; promptTemplate?: string }): Promise<{ success: boolean; prompt: UserPrompt }> => {
    const response = await api.put(`/api/shopping-lists/user-prompts/${promptId}`, data);
    return response.data;
};

export const deleteUserPrompt = async (promptId: string): Promise<{ success: boolean }> => {
    const response = await api.delete(`/api/shopping-lists/user-prompts/${promptId}`);
    return response.data;
};

export const regenerateUserPromptImage = async (promptId: string): Promise<{ success: boolean; promptId: string; status: string; referenceImage?: any }> => {
    const response = await api.post(`/api/shopping-lists/user-prompts/${promptId}/refimage:regenerate`);
    return response.data;
};

export const setPreferredStyle = async (promptId: string): Promise<{ success: boolean }> => {
    const response = await api.put('/api/shopping-lists/user/preferences/style', { promptId });
    return response.data;
};

// Task Lists: Character selection API
export const setListCharacter = async (listId: string, selectedCharacterId: string): Promise<{ success: boolean; listId: string; selectedCharacterId: string }> => {
    const response = await api.patch(`/api/shopping-lists/${listId}/character`, { selectedCharacterId });
    return response.data;
};

export default api;

// Account status API (referrals, entitlements)
export const getAccountStatus = async (): Promise<{
    rateLimit: { tier: 'trial' | 'starter' | 'pro' | 'platinum';[k: string]: any };
    hasQueuedReward: boolean;
}> => {
    const response = await api.get('/api/account/status');
    return response.data;
};

/**
 * @deprecated This function is deprecated and will be removed in a future version.
 * Use the new referral system API functions instead:
 * - `getUserReferralCode()` - Get user's permanent referral code
 * - `getReferralStats()` - Get referral statistics
 * - `trackReferralClick()` - Track referral clicks
 * - `completeReferralSignup()` - Complete referral signup
 *
 * The new system provides permanent referral codes that never change,
 * eliminating the need to generate new invite links.
 */
export const createReferralInvite = async (): Promise<{ inviteCode: string; linkToken: string; referral?: any }> => {
    const response = await api.post('/api/referrals/invite', {});
    return response.data;
};

// New referral system API functions
export const getUserReferralCode = async (): Promise<ReferralCodeInfo> => {
    const response = await api.get('/api/referrals/my-code');
    return response.data;
};

export const getReferralStats = async (): Promise<{ stats: ReferralStats }> => {
    const response = await api.get('/api/referrals/my-stats');
    return response.data;
};

export const trackReferralClick = async (referralCode: string, metadata?: { referrer?: string; userAgent?: string }): Promise<ReferralClickResult> => {
    const response = await api.post('/api/referrals/click', {
        referralCode,
        ...metadata
    });
    return response.data;
};

export const completeReferralSignup = async (referralCode: string, inviteeUserId: string): Promise<ReferralSignupResult> => {
    const response = await api.post('/api/referrals/complete-signup', {
        referralCode,
        inviteeUserId
    });
    return response.data;
};

export const validateReferralCode = async (code: string): Promise<any> => {
    const response = await api.get(`/api/referrals/code/${code}`);
    return response.data;
};

// Admin image model registry API
export const getAdminImageModels = async (): Promise<ImageModelRegistrySummary> => {
    const response = await api.get('/api/admin/image-models');
    return response.data;
};

export const updateAdminImageModel = async (
    modelSlug: string,
    updates: ImageModelUpdatePayload
): Promise<ImageModelConfig> => {
    const response = await api.put(`/api/admin/image-models/${encodeURIComponent(modelSlug)}`, updates);
    return response.data;
};

export const updateAdminTierModelChains = async (
    tierModelChain: TierModelChainConfig
): Promise<TierModelChainRecord> => {
    const response = await api.put('/api/admin/image-models/tier-chains', { tierModelChain });
    return response.data;
};

export const runAdminLatencyBenchmark = async (
    modelSlug: string
): Promise<{
    success: boolean;
    latencyMs: number;
    error?: string;
    benchmark: any;
    updatedModel: ImageModelConfig;
}> => {
    const response = await api.post(`/api/admin/image-models/${encodeURIComponent(modelSlug)}/benchmark`);
    return response.data;
};

export const getAdminImageModelEvaluations = async (params: {
    limit?: number;
    cursor?: string;
} = {}): Promise<ImageModelEvaluationListResult> => {
    const response = await api.get('/api/admin/image-models/evaluations', { params });
    return response.data;
};

export const getAdminImageModelEvaluation = async (
    runId: string
): Promise<ImageModelEvaluationDetail> => {
    const response = await api.get(`/api/admin/image-models/evaluations/${encodeURIComponent(runId)}`);
    return response.data;
};

export const resolveAdminImageModelEvaluation = async (
    payload: ImageModelEvaluationResolvePayload
): Promise<ImageModelEvaluationRun> => {
    const response = await api.post('/api/admin/image-models/evaluations/resolve', payload);
    return response.data;
};

export const generateAdminImageModelEvaluation = async (
    runId: string,
    modelSlug: string
): Promise<ImageModelEvaluationResult> => {
    const response = await api.post(`/api/admin/image-models/evaluations/${encodeURIComponent(runId)}/generate`, {
        modelSlug,
    });
    return response.data;
};

export const saveAdminImageModelEvaluationRating = async (
    runId: string,
    payload: ImageModelEvaluationRatingPayload
): Promise<ImageModelEvaluationResult> => {
    const response = await api.post(`/api/admin/image-models/evaluations/${encodeURIComponent(runId)}/ratings`, payload);
    return response.data;
};
